import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { sitesApi, pagesApi, blogApi, layoutsApi } from '../../../api/client';
import type { CreateSiteTemplateRequest } from '../../../schemas/validation';
import type { SiteManagerState } from '../useSiteManagerState';

interface SiteHandlerDependencies {
  siteId: string | undefined;
  state: SiteManagerState;
  loadBlogData: () => Promise<void>;
}

export const createSiteHandlers = ({ siteId, state, loadBlogData }: SiteHandlerDependencies) => {
  const {
    site,
    setSite,
    pages,
    setPages,
    setIsReordering,
    setActiveId,
    setShowSaveTemplateModal,
    setIsSavingTemplate,
    setShowPublishModal,
    isPublishing,
    setIsPublishing,
    showToast,
    setBlogData,
    setIsInstallingBlog,
    setIsUninstallingBlog,
    setShowBlogUninstallConfirm,
    layoutToDelete,
    setLayouts,
    setLayoutToDelete,
    setShowLayoutDeleteConfirm,
    setIsDeletingLayout,
  } = state;

  // Layout handlers
  const handleDeleteLayout = async () => {
    if (!layoutToDelete) return;

    try {
      setIsDeletingLayout(true);
      await layoutsApi.delete(layoutToDelete.id);
      setLayouts(prev => prev.filter(l => l.id !== layoutToDelete.id));
      setShowLayoutDeleteConfirm(false);
      setLayoutToDelete(null);
      showToast('Layout deleted successfully.');
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      console.error('Failed to delete layout:', error);
      showToast(apiError?.message || 'Failed to delete layout.', 'error', 5000);
    } finally {
      setIsDeletingLayout(false);
    }
  };

  // Blog handlers
  const handleInstallBlog = async () => {
    if (!siteId) return;

    try {
      setIsInstallingBlog(true);
      await blogApi.install(siteId);
      await loadBlogData();
      showToast('Blog app installed successfully!');
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      console.error('Failed to install blog:', error);
      showToast(apiError?.message || 'Failed to install blog app.', 'error', 5000);
    } finally {
      setIsInstallingBlog(false);
    }
  };

  const handleUninstallBlog = async () => {
    if (!siteId) return;

    try {
      setIsUninstallingBlog(true);
      await blogApi.uninstall(siteId);
      setBlogData(null);
      setShowBlogUninstallConfirm(false);
      showToast('Blog app uninstalled successfully.');
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      console.error('Failed to uninstall blog:', error);
      showToast(apiError?.message || 'Failed to uninstall blog app.', 'error', 5000);
    } finally {
      setIsUninstallingBlog(false);
    }
  };

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      const oldIndex = pages.findIndex(page => page.id === active.id);
      const newIndex = pages.findIndex(page => page.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const originalPages = [...pages];
        const newPages = arrayMove(pages, oldIndex, newIndex);
        const updatedPages = newPages.map((page, index) => ({
          ...page,
          orderPosition: index,
        }));

        setPages(updatedPages);

        const pageOrders = updatedPages.map((page, index) => ({
          pageId: page.id,
          orderPosition: index,
        }));

        try {
          setIsReordering(true);
          await pagesApi.reorderPages(siteId!, pageOrders);
        } catch (error) {
          console.error('API reorder failed, keeping local changes:', error);
          try {
            localStorage.setItem(`pages-${siteId}`, JSON.stringify(updatedPages));
          } catch (localError) {
            console.error('Failed to save to localStorage:', localError);
            setPages(originalPages);
            alert('Failed to reorder pages. Please try again.');
          }
        } finally {
          setIsReordering(false);
        }
      }
    }
  };

  // Template handlers
  const handleSaveAsTemplate = async (data: {
    name: string;
    description?: string;
    tags?: string;
    isPublic: boolean;
    isSystem: boolean;
  }) => {
    if (!site) return;

    try {
      setIsSavingTemplate(true);

      const requestData: CreateSiteTemplateRequest = { ...data };
      const response = await sitesApi.saveAsTemplate(site.id, requestData);

      showToast(`Template "${response.template.name}" saved successfully!`);
      setShowSaveTemplateModal(false);
    } catch (error: unknown) {
      console.error('Failed to save site template:', error);
      const apiError = error as { status?: number; message?: string; data?: unknown };

      let errorMessage = 'Failed to save site template.';
      if (apiError.status === 404 || apiError.status === 500 || !apiError.status) {
        errorMessage =
          'Site templates feature is not yet available on the backend. This feature will work once the API endpoints are implemented.';
      } else {
        if (error instanceof Error) {
          errorMessage += ` Error: ${error.message}`;
        }
        if (apiError?.status) {
          errorMessage += ` (Status: ${apiError.status})`;
        }
        if (apiError?.data) {
          errorMessage += ` Details: ${JSON.stringify(apiError.data)}`;
        }
      }
      alert(errorMessage);
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // Publish handlers
  const handlePublishSiteClick = () => {
    setShowPublishModal(true);
  };

  const handlePublishSiteConfirm = async () => {
    if (!site) return;

    const action = site.isPublished ? 'unpublish' : 'publish';

    try {
      setIsPublishing(true);

      const updatedSite = await sitesApi.publish(site.id, {
        isPublished: !site.isPublished,
      });

      setSite(updatedSite);
      setShowPublishModal(false);

      const successMsg = updatedSite.isPublished
        ? 'Website published successfully! 🚀'
        : 'Website unpublished successfully.';
      showToast(successMsg);

      console.log(`Website ${action}ed successfully:`, updatedSite);
    } catch (error: unknown) {
      console.error(`Failed to ${action} website:`, error);
      let errorMessage = `Failed to ${action} website.`;
      if (error instanceof Error) {
        errorMessage += ` Error: ${error.message}`;
      }
      showToast(errorMessage, 'error', 5000);
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishModalClose = () => {
    if (!isPublishing) {
      setShowPublishModal(false);
    }
  };

  // Favicon handler
  const handleFaviconUploaded = async (faviconUrl: string | null) => {
    try {
      const updatedSite = await sitesApi.update(siteId!, { faviconUrl });
      setSite(updatedSite);
      showToast(faviconUrl ? 'Favicon updated successfully!' : 'Favicon removed successfully!');
    } catch (error) {
      console.error('Failed to update favicon:', error);
      showToast('Failed to update favicon. Please try again.', 'error');
    }
  };

  // Helper functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return {
    handleDeleteLayout,
    handleInstallBlog,
    handleUninstallBlog,
    handleDragStart,
    handleDragEnd,
    handleSaveAsTemplate,
    handlePublishSiteClick,
    handlePublishSiteConfirm,
    handlePublishModalClose,
    handleFaviconUploaded,
    formatDate,
  };
};
